import { voiceRegistry } from '../voice/VoiceRegistry.js';
import OrbitersPlayMode from './play/OrbitersPlayMode.js';
import OrbitersEditMode from './edit/OrbitersEditMode.js';

export class OrbiterModeController {
  constructor({
    worldManager,
    scene,
    defaultMode = 'play',
    onModeChanged,
    emitParameterUpdate,
    parameterManager,
    // The DOM element this voice's orbiter theme is scoped to (its grid cell; null →
    // documentElement for single-orbiter). Passed through to edit mode, which re-applies design on
    // dimension changes.
    themeRoot = null,
    // This voice's id, threaded into edit mode so its dimension-changed event is stamped
    // per-voice (single-orbiter null → byte-identical).
    voiceId = null,
    playMode,
    editMode,
    // Asked by play mode when it builds the world, so the scene profile keeps one owner instead of
    // each builder reading the preference for itself.
    is3dOff,
  } = {}) {
    if (!worldManager && (!playMode || !editMode)) {
      throw new Error('[OrbiterModeController] worldManager or custom modes are required');
    }

    this.modes = {
      play: playMode ?? new OrbitersPlayMode({ worldManager, scene, is3dOff }),
      // Thread the voice's ParameterManager into edit mode (DI; no singleton).
      edit: editMode ?? new OrbitersEditMode({ worldManager, emitParameterUpdate, parameterManager, themeRoot, voiceId }),
    };

    if (emitParameterUpdate && this.modes.edit.setParameterEmitter) {
      this.modes.edit.setParameterEmitter(emitParameterUpdate);
    }

    this.onModeChanged = onModeChanged;
    this.activeModeKey = null;
    this.defaultMode = defaultMode;
    this.lastContext = null;
    // Optional gate on dimension switches (see setDimensionGate). Null = no gating (default).
    this._dimensionGate = null;
  }

  /**
   * Install (or clear, with null) a gate consulted before a dimension switch applies. Returning
   * false drops the switch. Used by shared (room) sessions so the interactive entry points
   * (keyboard, MIDI, the selector — they all route through setActiveDimension here) cannot switch
   * a voice this player does not control; remote-driven switches pass because the room client
   * applies them while its own gate is open. Outside a room nothing installs a gate.
   */
  setDimensionGate(gate) {
    this._dimensionGate = typeof gate === 'function' ? gate : null;
  }

  get activeMode() {
    return this.modes[this.activeModeKey] ?? null;
  }

  async init(context = {}) {
    return this.setMode(this.defaultMode, context);
  }

  async setMode(modeKey, context = undefined) {
    const nextMode = this.modes[modeKey];
    if (!nextMode) {
      throw new Error(`[OrbiterModeController] unknown mode "${modeKey}"`);
    }
    const resolvedContext = context ?? this.lastContext ?? {};
    if (!Object.keys(resolvedContext).length) {
      throw new Error('[OrbiterModeController] setMode requires a context on first call');
    }
    const switchingToSameMode = this.activeModeKey === modeKey;

    if (this.activeModeKey && !switchingToSameMode) {
      const currentMode = this.modes[this.activeModeKey];
      if (currentMode?.deactivate) {
        await currentMode.deactivate(resolvedContext);
      }
    }

    this.activeModeKey = modeKey;
    this.lastContext = resolvedContext;
    // The oscilloscope is per-voice; drive the active (focused) voice's instance.
    voiceRegistry.getActive()?.oscilloscope?.setRequirePlaybackState(modeKey !== 'edit');

    if (switchingToSameMode) {
      if (typeof nextMode.refresh === 'function') {
        return nextMode.refresh(resolvedContext);
      }
      return true;
    }

    const activationResult = await nextMode.activate(resolvedContext);
    this.onModeChanged?.(modeKey, resolvedContext);
    return activationResult;
  }

  getActiveMode() {
    return this.activeModeKey;
  }

  isEditMode() {
    return this.activeModeKey === 'edit';
  }

  getActiveDimensionId() {
    const active = this.activeMode;
    if (active?.getActiveDimensionId) {
      return active.getActiveDimensionId();
    }
    if (this.modes.edit?.getActiveDimensionId) {
      return this.modes.edit.getActiveDimensionId();
    }
    return null;
  }

  getAvailableDimensions() {
    if (this.modes.edit?.getAvailableDimensions) {
      return this.modes.edit.getAvailableDimensions();
    }
    const active = this.activeMode;
    if (active?.getAvailableDimensions) {
      return active.getAvailableDimensions();
    }
    return [];
  }

  setActiveDimension(dimensionId, options = {}) {
    if (this._dimensionGate && this._dimensionGate() === false) return false;
    let didChange = false;
    const active = this.activeMode;
    if (active && typeof active.setActiveDimension === 'function') {
      didChange = active.setActiveDimension(dimensionId, options) || didChange;
    }
    const editMode = this.modes.edit;
    if (editMode && editMode !== active && typeof editMode.setActiveDimension === 'function') {
      didChange = editMode.setActiveDimension(dimensionId, options) || didChange;
    }
    return didChange;
  }
}

export default OrbiterModeController;
